import React, { useState } from "react";

import { useSelector } from "react-redux";

import {
  AUDIENCE_OVERLAP_PROVIDERS,
  MAX_CREATORS_PER_PLATFORM_FOR_AUDIENCE_OVERLAP,
} from "src/app/components/AudienceOverlap/constants";
import { Button } from "src/app/components/Button/Button";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import TooltipPortal from "src/app/components/TooltipPortal/TooltipPortal";
import {
  getAudienceOverlapSocialProfiles,
  hasEnoughSocialProfilesForAudienceOverlap,
} from "src/app/CreatorShowcase/components/ShowcaseAudienceOverlap/audienceOverlapUtils";
import { ReactComponent as AudienceOverlapIcon } from "src/images/audience-overlap.svg";
import { RootState } from "src/redux/reducers";
import { ProjectPermissionsEnum } from "src/utils/PermissionsEnums";

import "./CreatorSearchAudienceOverlapButton.scss";
import CreatorSearchAudienceOverlapPane, {
  AudienceOverlapCreator,
} from "./CreatorSearchAudienceOverlapPane";

interface Props {
  creators: AudienceOverlapCreator[];
  workspaceUuid: string;
  projectId: string;
}

export default function CreatorSearchAudienceOverlapButton({
  creators,
  workspaceUuid,
  projectId,
}: Props) {
  const [showPane, setShowPane] = useState(false);

  const isAudienceOverlapEnabled = useSelector(
    (state: RootState) => state.mainReducer.identity?.isAudienceOverlapEnabled,
  );
  const canCalculate = useSelector((state: RootState) =>
    state.projectReducer.projectBasicData?.permissions?.project.includes(
      ProjectPermissionsEnum.CALCULATE_PROJECT_AUDIENCE_OVERLAP,
    ),
  );

  if (!isAudienceOverlapEnabled || !canCalculate) return null;

  const socialProfiles = getAudienceOverlapSocialProfiles(creators);
  // the overlap compares creators within one platform, so two accounts on the same one are needed
  const hasEnoughCreators =
    hasEnoughSocialProfilesForAudienceOverlap(socialProfiles);
  const hasTooManyCreators = AUDIENCE_OVERLAP_PROVIDERS.some(
    (provider) =>
      new Set(
        socialProfiles
          .filter((profile) => profile.socialProvider === provider)
          .map((profile) => profile.socialUsername.toLowerCase()),
      ).size > MAX_CREATORS_PER_PLATFORM_FOR_AUDIENCE_OVERLAP,
  );
  const canOpen = hasEnoughCreators && !hasTooManyCreators;

  return (
    <>
      <TooltipPortal
        content={
          hasTooManyCreators ? (
            <IDHFormattedMessage
              id="ws_audience_overlap_too_many_creators"
              defaultMessage="Audience overlap works for up to 100 creators on one platform"
            />
          ) : (
            <IDHFormattedMessage
              id="ws_audience_overlap_select_at_least_2_creators_on_same_platform"
              defaultMessage="Select at least 2 creators on the same platform"
            />
          )
        }
        contentHidden={canOpen}
        up
      >
        <Button
          size="medium"
          variant="white"
          className="audience-overlap-button"
          onClick={() => setShowPane(true)}
          data-qa="audience-overlap-button"
          disabled={!canOpen}
        >
          <AudienceOverlapIcon />
          <IDHFormattedMessage
            id="front_audience_overlap_title"
            defaultMessage="Audience overlap"
          />
        </Button>
      </TooltipPortal>
      {showPane && (
        <CreatorSearchAudienceOverlapPane
          creators={creators}
          workspaceUuid={workspaceUuid}
          projectId={projectId}
          onClose={() => setShowPane(false)}
        />
      )}
    </>
  );
}
